import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Archive, Bell, CheckCheck } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import type { NotificationDto } from '@cw/shared';
import { Button } from '@/design-system/components/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/design-system/components/popover';
import { Segmented } from '@/design-system/components/segmented';
import { EmptyState } from '@/design-system/components/states';
import { Tooltip } from '@/design-system/components/tooltip';
import { relativeTime } from '@/features/activity/activity-text';
import { api } from '@/lib/api';
import { hrefFor } from '@/lib/search';
import { cn } from '@/lib/utils';

type Filter = 'unread' | 'all' | 'archived';

/** Central de notificações (SPEC §42): não lidas, todas e arquivadas; atualiza ao vivo. */
export function NotificationBell({ portal = false }: { portal?: boolean }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState<Filter>('unread');
  const count = useQuery({
    queryKey: ['notifications', 'count'],
    queryFn: () => api.get<{ unread: number }>('/notifications/count'),
    refetchInterval: 120_000,
  });
  const list = useQuery({
    queryKey: ['notifications', 'list', filter],
    queryFn: () => api.get<NotificationDto[]>(`/notifications?filter=${filter}`),
    enabled: open,
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ['notifications'] });
  const mark = useMutation({
    mutationFn: (b: { id: string; read?: boolean; archived?: boolean }) =>
      api.patch(`/notifications/${b.id}`, b),
    onSuccess: refresh,
  });
  const readAll = useMutation({ mutationFn: () => api.post('/notifications/read-all'), onSuccess: refresh });
  const unread = count.data?.unread ?? 0;

  const openOne = (n: NotificationDto) => {
    if (!n.read) mark.mutate({ id: n.id, read: true });
    setOpen(false);
    // No portal, links internos levam ao início do portal.
    const href = n.href ?? '/';
    navigate(portal && !href.startsWith('/portal') ? '/portal' : hrefFor(href));
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip label="Notificações">
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={unread ? `Notificações: ${unread} não lidas` : 'Notificações'}
            className="relative"
          >
            <Bell />
            {unread > 0 && (
              <span className="tabular absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-signal px-1 text-[10px] font-semibold text-signal-fg">
                {unread > 99 ? '99+' : unread}
              </span>
            )}
          </Button>
        </PopoverTrigger>
      </Tooltip>
      <PopoverContent align="end" className="w-[min(380px,calc(100vw-24px))] p-0">
        <div className="flex items-center gap-2 border-b border-line p-3">
          <Segmented
            label="Mostrar"
            value={filter}
            onChange={setFilter}
            options={[
              { value: 'unread', label: 'Não lidas' },
              { value: 'all', label: 'Todas' },
              { value: 'archived', label: 'Arquivadas' },
            ]}
          />
          {unread > 0 && (
            <Tooltip label="Marcar todas como lidas">
              <Button
                size="icon-sm"
                variant="ghost"
                className="ml-auto"
                aria-label="Marcar todas como lidas"
                onClick={() => readAll.mutate()}
              >
                <CheckCheck />
              </Button>
            </Tooltip>
          )}
        </div>
        <div className="max-h-[60vh] overflow-y-auto p-1.5">
          {!list.data?.length ? (
            <EmptyState
              className="py-10"
              icon={<Bell />}
              title={filter === 'unread' ? 'Tudo em dia' : 'Nada por aqui'}
              description="Comentários, aprovações, prazos e pagamentos aparecem aqui."
            />
          ) : (
            list.data.map((n) => (
              <div key={n.id} className="group flex items-start gap-1 rounded-[10px] hover:bg-hover">
                <button
                  type="button"
                  onClick={() => openOne(n)}
                  className="flex min-w-0 flex-1 gap-2.5 px-2.5 py-2 text-left"
                >
                  <span
                    className={cn(
                      'mt-1.5 size-2 shrink-0 rounded-full',
                      n.read ? 'bg-transparent' : 'bg-signal',
                    )}
                  />
                  <span className="min-w-0 flex-1">
                    <span className={cn('block text-[13px] leading-snug', n.read && 'text-fg-muted')}>
                      {n.title}
                    </span>
                    {n.body && (
                      <span className="mt-0.5 line-clamp-2 block text-[12px] text-fg-subtle">{n.body}</span>
                    )}
                    <span className="mt-0.5 block text-[11.5px] text-fg-subtle">
                      {relativeTime(n.createdAt)}
                    </span>
                  </span>
                </button>
                {!n.archived && (
                  <button
                    type="button"
                    aria-label="Arquivar"
                    onClick={() => mark.mutate({ id: n.id, archived: true })}
                    className="m-1.5 rounded-[6px] p-1 text-fg-subtle opacity-0 group-hover:opacity-100 hover:text-fg focus:opacity-100"
                  >
                    <Archive className="size-3.5" />
                  </button>
                )}
              </div>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
